import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription, filter } from 'rxjs';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { AppState } from 'src/app/store/app.reducer';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PERMISSIONS_DEFAULT_SORT_COLUMN, PERMISSIONS_DEFAULT_SORT_COLUMN_DIRECTION, PERMISSIONS_PAGE_DEFAULT_SIZE } from 'src/app/conf/permissions';
import { Permission, EPermissionStatus } from 'src/app/model/permission';
import * as PermissionActions from 'src/app/store/permissions/permissions.actions';
import { selectLoading } from 'src/app/store/user-file/user-file.selector';
import { Site } from 'src/app/model/site';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectPermissions, selectPermissionsPaging, selectPermissionsAdded, selectPermissionsEdited, selectPermissionsDeleted, selectPermissionsPinResetted } from 'src/app/store/permissions/permissions.selector';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { TranslateService } from '@ngx-translate/core';
import { PermissionInfoDialogComponent } from '../permission-info-dialog/permission-info.dialog.component';
import { PermissionDialogComponent } from '../permission-dialog/permission.dialog.component';
import { PermissionsListFilters } from './permissions-list-filters/permissions-list-filters.component';

@Component({
	selector: 'app-permissions-list',
	templateUrl: './permissions-list.component.html',
	providers: [ConfirmationService, DialogService]
})

export class PermissionsListComponent implements OnInit, OnDestroy {
	permissionsLoading$: Observable<boolean | undefined>;
	permissionsLoadingSub: Subscription | undefined;

	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;

	permissions$: Observable<Permission[] | undefined>;
	permissionsSub: Subscription | undefined;

	paging$: Observable<ItemsPagination | undefined>;
	pagingSub: Subscription | undefined;

	added$: Observable<boolean | undefined>;
	addedSub: Subscription | undefined;

	edited$: Observable<boolean | undefined>;
	editedSub: Subscription | undefined;

	deleted$: Observable<boolean | undefined>;
	deletedSub: Subscription | undefined;

	pinResetted$: Observable<boolean | undefined>;
	pinResettedSub: Subscription | undefined;

	permissionsLoading: boolean;
	permissions: Permission[];
	site: Site;

	filter = '';
	activeFilter = true;
	readyFilter = true;
	expiredFilter = true;
	createdFilter = true;

	paging: PaginationParams = {
		size: PERMISSIONS_PAGE_DEFAULT_SIZE,
		page: 1,
		sort: PERMISSIONS_DEFAULT_SORT_COLUMN,
		order: PERMISSIONS_DEFAULT_SORT_COLUMN_DIRECTION,
	};
	pageSize: number = PERMISSIONS_PAGE_DEFAULT_SIZE;
	totalRecords: number;

	ref: DynamicDialogRef | undefined;
	acceptButtonStyle: string;
	rejectButtonStyle: string;

	flagImagePath = './assets/images/flags/';
	flagImageFormat = '.svg';

	constructor(
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.permissionsLoading$ = this.store.select(selectLoading);
		this.site$ = this.store.select(selectCurrentSite);
		this.permissions$ = this.store.select(selectPermissions);
		this.paging$ = this.store.select(selectPermissionsPaging);
		this.added$ = this.store.select(selectPermissionsAdded);
		this.edited$ = this.store.select(selectPermissionsEdited);
		this.deleted$ = this.store.select(selectPermissionsDeleted);
		this.pinResetted$ = this.store.select(selectPermissionsPinResetted);
	}

	ngOnInit(): void {
		this.subscribeAll();
	}

	ngOnDestroy(): void {
		this.permissionsLoadingSub?.unsubscribe();
		this.permissionsSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.pagingSub?.unsubscribe();
		this.addedSub?.unsubscribe();
		this.editedSub?.unsubscribe();
		this.deletedSub?.unsubscribe();
		this.pinResettedSub?.unsubscribe();
	}

	subscribeAll(): void {
		this.permissionsLoadingSub = this.permissionsLoading$?.subscribe(
			(permissionsLoading) => {
				this.permissionsLoading = permissionsLoading;
			}
		);
		this.permissionsSub = this.permissions$?.subscribe(
			(permissions) => {
				this.permissions = permissions;
			}
		);
		this.siteSub = this.site$.pipe(filter(s => !!s)).subscribe((site) => {
			this.site = site;
			if (this.site?.CurrentlyOwned) {
				this.getPermissions();
				this.store.dispatch(PermissionActions.getPermittedCommands());
				this.store.dispatch(PermissionActions.getPermittedThs());
			} else {
				this.permissions = [];
				this.totalRecords = 0;
			}
		});
		this.pagingSub = this.paging$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			}
		);
		this.addedSub = this.added$.pipe(filter(added => !!added))
			.subscribe(() => {
				this.getPermissions();
			}
		);
		this.editedSub = this.edited$.pipe(filter(edited => !!edited))
			.subscribe(() => {
				this.getPermissions();
			}
		);
		this.deletedSub = this.deleted$.pipe(filter(deleted => !!deleted))
			.subscribe(() => {
				this.getPermissions();
			}
		);
		this.pinResettedSub = this.pinResetted$.pipe(filter(reset => !!reset))
			.subscribe(() => {
				this.getPermissions();
			}
		);
	}

	// filters is applied when permissions-list-filters emit the filtersChanged event
	// applyFilters is called in permissions-tab-group.component.html
	applyFilters(filters: PermissionsListFilters): void {
		this.filter = filters.filter;
		this.activeFilter = filters.activeFilter;
		this.readyFilter = filters.readyFilter;
		this.expiredFilter = filters.expiredFilter;
		this.createdFilter = filters.createdFilter;
		this.paging = { ...this.paging, page: 1 };
		this.getPermissions();
	}

	expired(permission: Permission): boolean {
		return permission.Status === EPermissionStatus.Expired;
	}

	editPermission(permission: Permission): void {
		this.store.dispatch(PermissionActions.resetPermissionForm());
		this.ref = this.dialogService.open(PermissionDialogComponent, {
			header: this.translate.instant('EDIT_PERMISSION'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				permission: permission
			}
		});
	}

	info(permission: Permission): void {
		this.store.dispatch(PermissionActions.resetPermissionForm());
		this.ref = this.dialogService.open(PermissionInfoDialogComponent, {
			header: this.translate.instant('PERMISSION') + ': ' + permission.Name,
			width: '80vw',
			breakpoints: { '1200px': '98vw' },
			contentStyle: { overflow: 'auto' },
			focusOnShow: false, // disable default focus on the close button
			data: { permission: permission }
		});
	}

	getVisiblePin(permission: Permission): boolean {
		return permission.PinVisible;
	}

	setVisiblePin(permission: Permission) {
		if (permission.PinVisible === undefined) {
			permission.PinVisible = false;
		}
		permission.PinVisible = !permission.PinVisible;
	}

	resetPin(permissionId: number) {
		this.acceptButtonStyle = 'p-button-danger';
		this.rejectButtonStyle = 'p-button-secondary';
		this.confirmationService.confirm({
			header: this.translate.instant('RESET_PERMISSION_PIN'),
			message: this.translate.instant('NEW_RESET_PERMISSION_PIN_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(PermissionActions.resetPin({ permissionId }));
			},
			reject: () => { }
		});
	}

	deletePer(permission: Permission): void {
		this.acceptButtonStyle = 'p-button-danger';
		this.rejectButtonStyle = 'p-button-secondary';
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_PERMISSION'),
			message: this.translate.instant('DELETE_PERMISSION_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(PermissionActions.deletePermission({ permission }));
			},
			reject: () => { }
		});
	}

	getColorStatus(status: string): string {
		switch (status) {
			case 'expired':
				return 'danger';
			case 'active':
				return 'success';
			case 'ready':
				return 'warning';
			default:
				return 'info';
		}
	}

	loadPermissions(event: TableLazyLoadEvent) {
		this.paging = pageSortChange(this.paging, event);
		this.getPermissions();
	}

	getPermissions() {
		if (this.site !== undefined) {
			this.store.dispatch(PermissionActions.getPermissions({
				paging: this.paging, filter: this.filter,
				activeFilter: this.activeFilter, readyFilter: this.readyFilter,
				expiredFilter: this.expiredFilter, createdFilter: this.createdFilter
			}));
		}
	}

	getCurrentLanguageFlag(customerLanguage): string {
		if (customerLanguage === 'en') {
			customerLanguage = 'en-US';
		}
		return this.flagImagePath + customerLanguage + this.flagImageFormat;
	}
}
